import {catchError, finalize, map, mergeMap, switchMap, take, tap} from 'rxjs/operators';
import {UntilDestroy} from '@ngneat/until-destroy';
import {inject, Injectable} from '@angular/core';
import type {BootstrapResult} from '@app/core/interfaces';
import {Router} from '@angular/router';
import {Observable, of} from 'rxjs';

import {StoreService} from '@shared/services/store.service';
import {ModalService} from '@shared/services/modal.service';
import {ToastifyService} from '@shared/services/toastify.service';
import {HelpService} from '@shared/components/help/services/help.service';
import {AppApiService} from '@app/core/services/app-api.service';
import {NotificationApiService} from '@app/main/modules/notification/api/notification-api.service';
import {WorkspaceService} from '@shared/services/workspace.service';


@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class BootstrapAppService {

  private revertToPersonalInProgress = false;
  private readonly appApiService = inject(AppApiService);

  private readonly notificationApiService = inject(NotificationApiService);

  constructor(
    private storeService: StoreService,
    private modalService: ModalService,
    private toastifyService: ToastifyService,
    private helpService: HelpService,
    private workspaceService: WorkspaceService,
    private router: Router,
  ) {
  }

  /**
   * وقتی دسترسی Assigned قطع می‌شود (403/240 یا sync ناموفق پس از disable/delete/block)،
   * کاربر را به Personal خودش برمی‌گرداند، header را پاک و bootstrap را با داده خودش refetch می‌کند.
   * روی حساب Personal یا در صورت درخواست همزمان no-op است.
   */
  revertToPersonalAndRefetch(): Observable<BootstrapResult | null> {
    if (this.revertToPersonalInProgress || !this.workspaceService.isAssignedWorkspace()) {
      return of(null);
    }

    this.revertToPersonalInProgress = true;
    this.workspaceService.selectPersonal();

    return this.refetchBootstrapper(true).pipe(
      tap(() => this.toastifyService.info('دسترسی شما به این حساب قطع شد؛ به حساب شخصی بازگردانده شدید.')),
      catchError(() => of(null)),
      finalize(() => this.revertToPersonalInProgress = false),
    );
  }

  storeBootstrapperData(): Observable<boolean> {
    return of(true)
      .pipe(
        take(1),
        tap(() => this.workspaceService.prepareInitialContext()),
        mergeMap(() => this.fetchOrGetDataBootstrap()),
        tap((bootstrap: BootstrapResult) => {
          this.applyBootstrap(bootstrap);
        }),
        switchMap((bootstrap: BootstrapResult) => {
          if (this.workspaceService.needsBootstrapRefetchAfterInit()) {
            this.workspaceService.clearPendingBootstrapRefetch();
            return this.refetchBootstrapper();
          }
          return of(bootstrap);
        }),
        map((response: BootstrapResult): boolean => {
          return response.userData.shouldAskForPurposes;
        }),
      );
  }

  refetchBootstrapper(redirectToDashboard = false): Observable<BootstrapResult> {
    return this.appApiService.getBootstrapper().pipe(
      map((resp) => resp.result),
      tap((bootstrap: BootstrapResult) => {
        this.applyBootstrap(bootstrap);
        this.workspaceService.clearPendingBootstrapRefetch();
        if (redirectToDashboard) {
          this.workspaceService.notifyWorkspaceContextChanged();
          this.navigateToDashboardAfterContextChange();
        }
      }),
    );
  }

  private navigateToDashboardAfterContextChange(): void {
    const dashboardPath = '/dashboard';
    const currentPath = this.router.url.split('?')[0];

    if (currentPath === dashboardPath) {
      return;
    }

    void this.router.navigate([dashboardPath]);
  }

  bootstrapperNotify(result: BootstrapResult): void {
    const entryNotifications = result.notificationsData.unreadCustomNotifications.filter(
      (n) => n.showAtEntry,
    );
    if (!entryNotifications.length) {
      return;
    }

    this.modalService.BootstrapperNotify(entryNotifications).then((ids) => {
      if (!ids?.length) {
        return;
      }

      this.notificationApiService.markCustomNotificationsAsRead({ids}).subscribe(() => {
        const baseData = this.storeService.getBaseData();
        if (!baseData) {
          return;
        }
        this.storeService.setBaseData({
          ...baseData,
          notificationsData: {
            ...baseData.notificationsData,
            totalUnreadCustomNotificationsCount:
              baseData.notificationsData.totalUnreadCustomNotificationsCount - ids.length,
          },
        });
      });
    });
  }

  fetchOrGetDataBootstrap(): Observable<BootstrapResult> {
    return this.storeService.baseData$.pipe(
      take(1),
      switchMap((localValue) => {
        if (localValue) {
          return of(localValue);
        }

        return this.appApiService.getBootstrapper().pipe(
          map((resp) => resp.result),
          tap((result: BootstrapResult) => {
            if (!result.userData.shouldAskForPurposes) {
              this.bootstrapperNotify(result);
            }
          }),
        );
      }),
    );
  }

  private applyBootstrap(bootstrap: BootstrapResult): void {
    this.helpService.helpItems = bootstrap.helpItems;
    this.storeService.setBaseData(bootstrap);
    this.workspaceService.initAfterBootstrap(bootstrap);
  }
}
